import { Phone, Truck, Wrench, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import useCallTracking from "@/hooks/useCallTracking";
import { telHref } from "@/constants/site";
import { useT } from "@/i18n/LanguageContext";

const ICONS = [Phone, Truck, Wrench];

/**
 * "How it works" — 3-step process strip. Sets clear expectations of what
 * happens when someone calls, killing hesitation before booking.
 */
export default function HowItWorks() {
  const { t } = useT();
  const trackCall = useCallTracking();
  const steps = t.howItWorks.steps;

  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative bg-white dark:bg-[#0A0A0A] py-16 sm:py-24 lg:py-28 border-y border-black/5"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <Reveal>
          <div className="max-w-2xl">
            <div className="overline text-[#1E63FF]">{t.howItWorks.overline}</div>
            <h2 className="font-display font-semibold text-3xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A0A0A] dark:text-white mt-4 leading-[1.02]">
              {t.howItWorks.heading1}<br />
              <span className="text-neutral-400 dark:text-neutral-500">{t.howItWorks.heading2}</span>
            </h2>
          </div>
        </Reveal>

        <div className="mt-12 lg:mt-16 grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 relative">
          {/* Horizontal connector line for desktop */}
          <div className="hidden md:block absolute top-14 left-[16.6%] right-[16.6%] h-px border-t border-dashed border-black/15 dark:border-white/15" aria-hidden="true" />

          {steps.map((s, i) => {
            const Icon = ICONS[i] || Phone;
            return (
              <Reveal key={i} delay={i * 120}>
                <div
                  data-testid={`how-it-works-step-${i + 1}`}
                  className="relative group p-6 sm:p-7 rounded-2xl bg-[#F5F5F7] dark:bg-white/[0.03] border border-black/10 dark:border-white/10 hover:border-[#1E63FF]/40 transition-colors h-full"
                >
                  {/* Step number */}
                  <div className="flex items-center gap-4">
                    <div className="relative w-14 h-14 rounded-full bg-white dark:bg-[#0A0A0A] border-2 border-[#1E63FF] flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#1E63FF]" strokeWidth={1.8} />
                      <span className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-[#1E63FF] text-white text-xs font-bold flex items-center justify-center">
                        {i + 1}
                      </span>
                    </div>
                    {i < steps.length - 1 && (
                      <ArrowRight className="hidden md:block w-5 h-5 text-neutral-400 ml-auto opacity-60 group-hover:text-[#1E63FF] group-hover:translate-x-1 transition-all" strokeWidth={1.8} />
                    )}
                  </div>

                  <h3 className="mt-5 font-display font-semibold text-lg sm:text-xl tracking-tight text-[#0A0A0A] dark:text-white">
                    {s.title}
                  </h3>
                  <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">
                    {s.desc}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <Reveal delay={100}>
          <div className="mt-10 lg:mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-sm text-neutral-500 max-w-xl">
              {t.howItWorks.footnote}
            </p>
            <a
              data-testid="how-it-works-call-btn"
              href={telHref}
              onClick={trackCall("how-it-works")}
              className="btn-blue h-12 px-6 rounded-full inline-flex items-center gap-2.5 text-sm font-semibold pulse-blue"
            >
              <Phone className="w-4 h-4" strokeWidth={2.2} />
              {t.howItWorks.startNow}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
